import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenantLocation } from '../context/LocationContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  RefreshCw,
  Loader2,
  Warehouse,
  AlertTriangle,
  Package,
  MapPin,
  TrendingDown,
  Sparkles,
  MessageCircleQuestion,
  CheckCircle2,
  Undo2,
} from 'lucide-react';
import { cn } from '../lib/utils';

export default function Inventory() {
  const { token } = useAuth();
  const { currentLocationId } = useTenantLocation();
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showMismatchOnly, setShowMismatchOnly] = useState(false);

  // A1 Triage state
  const [triaging, setTriaging] = useState(false);
  const [triageResult, setTriageResult] = useState(null);
  const [actions, setActions] = useState([]);
  const [busyActionId, setBusyActionId] = useState(null);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const response = await api.getInventory(token, showMismatchOnly);
      setInventory(response.data);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showMismatchOnly, currentLocationId]);

  const loadAgentActions = async () => {
    try {
      const r = await api.listAgentActions(token);
      setActions(r.data.actions || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    loadAgentActions();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTriage = async () => {
    setTriaging(true);
    setTriageResult(null);
    try {
      const r = await api.triageAlerts(token);
      setTriageResult(r.data);
      if (r.data.error) toast.error(r.data.error);
      else toast.success(`Triaged ${r.data.total_items_triaged || 0} items into ${(r.data.clusters || []).length} clusters`);
      await loadAgentActions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Triage failed');
    } finally {
      setTriaging(false);
    }
  };

  const askAboutRow = (item) => {
    const q = `Why does SKU ${item.sku} (${item.product_name}) show different quantities in UnTill (${item.utill_qty}) and Odoo (${item.odoo_qty})?`;
    navigate(`/logs?ask=${encodeURIComponent(q)}`);
  };

  const handleConfirmAction = async (actionId) => {
    setBusyActionId(actionId);
    try {
      await api.confirmAgentAction(token, actionId);
      toast.success('Marked as reviewed');
      await loadAgentActions();
    } catch (e) {
      toast.error('Failed to confirm');
    } finally {
      setBusyActionId(null);
    }
  };

  const handleUndoAction = async (actionId) => {
    setBusyActionId(actionId);
    try {
      await api.undoAgentAction(token, actionId);
      toast.success('Action undone');
      await loadAgentActions();
    } catch (e) {
      toast.error('Failed to undo');
    } finally {
      setBusyActionId(null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await api.syncInventory(token);
      toast.success(result.data.message);
      fetchInventory();
    } catch (error) {
      toast.error('Inventory sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const getMismatchSeverity = (utill, odoo) => {
    const diff = Math.abs(utill - odoo);
    if (diff > 50) return 'high';
    if (diff > 20) return 'medium';
    return 'low';
  };

  const mismatchCount = inventory.filter(i => i.mismatch).length;
  const totalUtillQty = inventory.reduce((sum, i) => sum + i.utill_qty, 0);
  const totalOdooQty = inventory.reduce((sum, i) => sum + i.odoo_qty, 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">
            Inventory
          </h1>
          <p className="text-slate-400 mt-1">Track and sync stock levels between UnTill and Odoo</p>
        </div>
        <Button 
          onClick={handleSync}
          disabled={syncing}
          className="bg-blue-500 hover:bg-blue-600 text-white"
          data-testid="sync-inventory-btn"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Sync Inventory
        </Button>
      </div>

      {/* Mismatch Alert */}
      {mismatchCount > 0 && (
        <Card className="bg-rose-500/10 border-rose-500/20">
          <CardContent className="p-4 flex items-center gap-4">
            <AlertTriangle className="w-6 h-6 text-rose-400" />
            <div>
              <p className="text-rose-400 font-medium">
                {mismatchCount} inventory mismatches detected
              </p>
              <p className="text-rose-400/70 text-sm">
                Stock levels between UnTill and Odoo don&apos;t match. Review and sync to resolve.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* A1 — AI Alert Triage */}
      <Card
        className="bg-gradient-to-br from-purple-500/[0.04] via-slate-900/60 to-slate-900/40 border-purple-500/20"
        data-testid="alert-triage-card"
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="text-base font-heading text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-300" />
                AI Alert Triage
                <span className="ml-2 text-[10px] uppercase tracking-wider text-purple-300/70 border border-purple-500/30 rounded px-1.5 py-0.5">
                  beta · human-approved actions
                </span>
              </CardTitle>
              <CardDescription className="text-slate-500 text-xs mt-1">
                Cluster your open mismatches, explain each one, and recommend the safest next step — nothing is applied until you confirm.
              </CardDescription>
            </div>
            <Button
              onClick={handleTriage}
              disabled={triaging}
              className="bg-purple-500 hover:bg-purple-600 text-white"
              data-testid="triage-run-btn"
            >
              {triaging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {triaging ? 'Investigating…' : 'Triage all mismatches'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {triageResult?.clusters?.length > 0 && (
            <div className="space-y-2" data-testid="triage-clusters">
              {triageResult.clusters.map((c, i) => (
                <div
                  key={c.cluster_id || i}
                  className="rounded-lg border border-slate-800 bg-slate-900/60 p-3"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[280px]">
                      <div className="text-sm text-slate-100">{c.summary}</div>
                      <div className="text-xs text-slate-500 mt-1">{c.reasoning}</div>
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                        <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 font-mono">
                          {(c.affected_skus || []).length} SKU{(c.affected_skus || []).length === 1 ? '' : 's'}
                        </span>
                        <span className="px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/20">
                          {(c.likely_cause || 'other').replace(/_/g, ' ')}
                        </span>
                        <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                          confidence {Math.round((c.confidence || 0) * 100)}%
                        </span>
                        <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">
                          action: {(c.recommended_action || 'none').replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {triageResult?.error && (
            <div className="text-sm text-rose-300 border border-rose-500/30 rounded-md p-3 bg-rose-500/10">
              {triageResult.error}
            </div>
          )}
          {actions.length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                Agent action log · reversible for 30 days
              </div>
              <div className="rounded-lg border border-slate-800 divide-y divide-slate-800/70 max-h-56 overflow-y-auto">
                {actions.slice(0, 20).map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2 px-3 py-2 text-xs"
                    data-testid={`agent-action-${a.id}`}
                  >
                    <span
                      className={cn(
                        'px-1.5 py-0.5 rounded font-mono text-[10px]',
                        a.status === 'confirmed'
                          ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20'
                          : a.status === 'undone'
                          ? 'bg-slate-700/40 text-slate-400 line-through'
                          : 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                      )}
                    >
                      {a.status}
                    </span>
                    <span className="text-slate-300 truncate flex-1">
                      {a.summary || a.note || a.action}
                    </span>
                    <span className="text-slate-600 font-mono text-[10px]">
                      {new Date(a.created_at).toLocaleTimeString()}
                    </span>
                    {a.status !== 'undone' && a.status !== 'confirmed' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-emerald-300 hover:text-white hover:bg-emerald-500/10"
                        onClick={() => handleConfirmAction(a.id)}
                        disabled={busyActionId === a.id}
                        data-testid={`confirm-action-${a.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Confirm
                      </Button>
                    )}
                    {a.status !== 'undone' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-slate-400 hover:text-white hover:bg-slate-800"
                        onClick={() => handleUndoAction(a.id)}
                        disabled={busyActionId === a.id}
                        data-testid={`undo-action-${a.id}`}
                      >
                        <Undo2 className="w-3 h-3 mr-1" /> Undo
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!triageResult && actions.length === 0 && (
            <p className="text-xs text-slate-500">
              Click <span className="text-purple-300">Triage all mismatches</span> to have the AI cluster and explain every open mismatch — takes 15–25s.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Items</p>
                <p className="text-2xl font-bold text-white mt-1">{inventory.length}</p>
              </div>
              <Package className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">UnTill Stock</p>
                <p className="text-2xl font-bold text-blue-400 mt-1">{totalUtillQty.toLocaleString()}</p>
              </div>
              <Warehouse className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Odoo Stock</p>
                <p className="text-2xl font-bold text-emerald-400 mt-1">{totalOdooQty.toLocaleString()}</p>
              </div>
              <Warehouse className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className={cn("border-slate-800", mismatchCount > 0 ? "bg-rose-500/10" : "bg-slate-900/50")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Mismatches</p>
                <p className={cn("text-2xl font-bold mt-1", mismatchCount > 0 ? "text-rose-400" : "text-slate-500")}>
                  {mismatchCount}
                </p>
              </div>
              <AlertTriangle className={cn("w-8 h-8", mismatchCount > 0 ? "text-rose-400" : "text-slate-600")} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Toggle */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <span className="text-white font-medium">Show Mismatches Only</span>
            </div>
            <Switch
              checked={showMismatchOnly}
              onCheckedChange={setShowMismatchOnly}
              data-testid="mismatch-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Inventory Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : inventory.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No inventory items found. Seed demo data from the dashboard.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">SKU</TableHead>
                  <TableHead className="text-slate-400">Product</TableHead>
                  <TableHead className="text-slate-400">Location</TableHead>
                  <TableHead className="text-slate-400">UnTill Qty</TableHead>
                  <TableHead className="text-slate-400">Odoo Qty</TableHead>
                  <TableHead className="text-slate-400">Difference</TableHead>
                  <TableHead className="text-slate-400">Last Sale</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inventory.map((item) => {
                  const diff = item.utill_qty - item.odoo_qty;
                  const severity = getMismatchSeverity(item.utill_qty, item.odoo_qty);
                  return (
                    <TableRow 
                      key={item.id} 
                      className={cn(
                        "border-slate-800 data-table-row",
                        item.mismatch && "bg-rose-500/5"
                      )}
                    >
                      <TableCell className="font-mono text-white">{item.sku}</TableCell>
                      <TableCell className="text-slate-300">{item.product_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-slate-400">
                          <MapPin className="w-3 h-3" />
                          {item.location}
                        </div>
                      </TableCell>
                      <TableCell className="text-blue-400 font-mono">{item.utill_qty}</TableCell>
                      <TableCell className="text-emerald-400 font-mono">{item.odoo_qty}</TableCell>
                      <TableCell>
                        {diff !== 0 ? (
                          <span className={cn(
                            "font-mono font-medium",
                            diff > 0 ? "text-emerald-400" : "text-rose-400"
                          )}>
                            {diff > 0 ? '+' : ''}{diff}
                          </span>
                        ) : (
                          <span className="text-slate-500">0</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-slate-400">
                          <TrendingDown className="w-3 h-3" />
                          {item.last_sale_qty}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {item.mismatch ? (
                            <Badge className={cn(
                              severity === 'high' ? 'status-error' :
                              severity === 'medium' ? 'status-pending' : 'status-synced'
                            )}>
                              Mismatch
                            </Badge>
                          ) : (
                            <Badge className="status-success">Synced</Badge>
                          )}
                          {item.mismatch && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-purple-300 hover:text-white hover:bg-purple-500/10"
                              onClick={() => askAboutRow(item)}
                              title="Ask the assistant about this item"
                              data-testid={`ask-about-${item.sku}`}
                            >
                              <MessageCircleQuestion className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
